import {Action, Selector, State, StateContext} from '@ngxs/store';
import {ValidationStateModel} from '../../modules/validation/models/validation-state.model';
import {GeneralContract} from '../../models/api/product/base.model';
import {PolicyGreenCardAddActions} from './policy-green-card-add.actions';
import {NavService} from '../../services/nav.service';
import {Injectable, NgZone} from '@angular/core';
import {ModalService} from '../../services/modal.service';
import {SourcePolicySearchComponent} from '../../modules/shared/components/source-policy-search/source-policy-search.component';
import {NavItemCodes} from '../../enums/nav-item-codes.enum';
import {TranslateService} from '@ngx-translate/core';
import {skipInterceptors} from '../../helpers/request.helper';
import {catchError, tap} from 'rxjs/operators';
import {HttpErrorResponse} from '@angular/common/http';
import {ValidationActions} from '../../modules/validation/state/validation.actions';
import {of} from 'rxjs';
import {instanceToPlain} from 'class-transformer';
import {NotificationService} from '../../services/notification.service';
import {Calculation} from '../../models/api/calculation.model';
import {CalculationService} from '../../services/calculation.service';
import {PolicyService} from '../../services/policy.service';
import {SourcePolicySearchSearch} from '../../modules/shared/components/source-policy-search/source-policy-search.model';
import ContextType = SourcePolicySearchSearch.ContextType;
import ModelType = SourcePolicySearchSearch.ModelType;

interface PolicyGreenCardAddStateModel extends ValidationStateModel {
    generalContract: GeneralContract;
    activeCalculation?: Calculation;
    vehicleMakeList: string[];
}

@State<PolicyGreenCardAddStateModel>({
    name: PolicyGreenCardAddState.STATE_NAME,
    defaults: {
        generalContract: null,
        activeCalculation: null,
        validationErrors: null,
        vehicleMakeList: null,
    },
})
@Injectable()
export class PolicyGreenCardAddState {
    static STATE_NAME = 'policyGreenCardAdd';

    constructor(
        protected navService: NavService,
        protected ngZone: NgZone,
        protected modalService: ModalService,
        protected translate: TranslateService,
        protected calculationService: CalculationService,
        protected notificationService: NotificationService,
        protected policyService: PolicyService
    ) {}

    @Selector()
    static generalContract(state: PolicyGreenCardAddStateModel) {
        return state.generalContract;
    }

    @Selector()
    static activeCalculation(state: PolicyGreenCardAddStateModel) {
        return state.activeCalculation;
    }

    @Selector()
    static vehicleMakeList(state: PolicyGreenCardAddStateModel) {
        return state.vehicleMakeList;
    }

    @Action(PolicyGreenCardAddActions.SetGeneralContract)
    setGeneralContract(
        ctx: StateContext<PolicyGreenCardAddStateModel>,
        {generalContract}: PolicyGreenCardAddActions.SetGeneralContract
    ) {
        ctx.patchState({
            generalContract,
        });
    }

    @Action(PolicyGreenCardAddActions.SetCalculation)
    setCalculation(
        ctx: StateContext<PolicyGreenCardAddStateModel>,
        {calculation}: PolicyGreenCardAddActions.SetCalculation
    ) {
        ctx.patchState({
            activeCalculation: calculation,
        });
    }

    @Action(PolicyGreenCardAddActions.SearchModal)
    searchModal(
        ctx: StateContext<PolicyGreenCardAddStateModel>,
        {generalContractId}: PolicyGreenCardAddActions.SearchModal
    ) {
        return this.ngZone.run(
            () =>
                this.modalService.generic({
                    nzTitle: this.translate.instant('NAV.POLICY_ADD_GREEN_CARD.LABEL'),
                    nzOkText: this.translate.instant('LABELS.SEARCH'),
                    nzContent: SourcePolicySearchComponent,
                    nzData: {
                        generalContractId,
                        modelType: ModelType.calculation,
                        contextType: ContextType.greenCardAdd,
                    } as Partial<SourcePolicySearchComponent>,
                    nzOnOk: async (instance: SourcePolicySearchComponent) => {
                        try {
                            const responseModel = await instance.search().toPromise();
                            // pass policy object to resolver
                            const navExtras = {
                                state: responseModel,
                            };
                            const payload = {
                                generalContractId,
                                policyNumber: instance.policyNumber.value,
                            };
                            this.navService.navigateByCode(
                                NavItemCodes.POLICY_GREEN_CARD_ADD,
                                payload,
                                undefined,
                                navExtras
                            );
                            return true;
                        } catch (e) {
                            return false;
                        }
                    },
                }).afterClose
        );
    }

    @Action(PolicyGreenCardAddActions.SaveCalculation)
    saveCalculation(
        ctx: StateContext<PolicyGreenCardAddStateModel>,
        {calculation, productCode}: PolicyGreenCardAddActions.SaveCalculation
    ) {
        const headers = skipInterceptors([409]);
        return this.calculationService.saveCalculation(instanceToPlain(calculation), productCode, headers).pipe(
            tap((res: Calculation) => {
                this.notificationService.success('CALCULATION.CREATED');
                this.navService.navigateByCode(NavItemCodes.CALCULATION_PREVIEW, {calculationId: res?.id});
            }),
            catchError((rej: HttpErrorResponse) => {
                ctx.dispatch(new ValidationActions.Set(PolicyGreenCardAddState.STATE_NAME, rej.error));
                return of(null);
            })
        );
    }

    @Action(PolicyGreenCardAddActions.SetVehicleMakeList)
    setVehicleMakeList(
        ctx: StateContext<PolicyGreenCardAddStateModel>,
        {vehicleMakeList}: PolicyGreenCardAddActions.SetVehicleMakeList
    ) {
        ctx.patchState({vehicleMakeList});
    }

    @Action(PolicyGreenCardAddActions.Clear)
    clear(ctx: StateContext<PolicyGreenCardAddStateModel>) {
        ctx.patchState({
            activeCalculation: null,
        });
    }
}
